import { Koi } from '@ksyos/koi';
import type { IAssets } from '../assets';
import type { ISchemaContainer } from '../schemaContainer';
import { CardioPerfectEthnicGroup } from '../../enums/cardioPerfectEthnicGroup';
import { CareFusionEthnicGroup } from '../../enums/careFusionEthnicGroup';
import { ConclusieDiagnoseOption } from '../../enums/conclusieDiagnoseOption';
import { Gender } from '../../enums/gender';
import { GenderSpirometer } from '../../enums/genderSpirometer';
import { HardwareProcessStatus } from '../../enums/hardwareProcessStatus';
import { assets } from '../assets';

export interface IPulmoKlachtenCustomControlDataValidated {
    [key: string]: string | boolean | null;
    aantal_exacerbaties_in_het_afgelopen_jaar: string | null;
    allergie_text: string | null;
    allergie_value: boolean;
    astma_in_de_familie_text: string | null;
    astma_in_de_familie_value: boolean;
    bekend_met_astma_text: string | null;
    bekend_met_astma_value: boolean;
    bekend_met_COPD_text: string | null;
    bekend_met_COPD_value: boolean;
    bekend_met_hartfalen_text: string | null;
    bekend_met_hartfalen_value: boolean;
    beroep: string | null;
    hyperreactiviteit_text: string | null;
    hyperreactiviteit_value: boolean;
    KNO_klachten_text: string | null;
    KNO_klachten_value: boolean;
    kortademig_s_nachts_text: string | null;
    kortademig_s_nachts_value: boolean;
    maagklachten_text: string | null;
    maagklachten_value: boolean;
    rookgedrag: string | null;
    slijm_opgeven_text: string | null;
    slijm_opgeven_value: boolean;
}

export enum SmokingBehaviour {
    exRoker = 'Ex-roker',
    nooit = 'Nooit',
    roker = 'Roker',
}

export interface ITobaccoUse {
    duration?: string;
    smokingBehaviour: SmokingBehaviour;
    startDate: Date | string;
}

export const rookgedragOpties = [SmokingBehaviour.nooit, SmokingBehaviour.exRoker, SmokingBehaviour.roker];
export const rokerDuurOpties = [
    'Roker 1 tot 10 jaar',
    'Roker 11 tot 20 jaar',
    'Roker 21 tot 30 jaar',
    'Roker 31 tot 40 jaar',
    'Roker 40-plus jaar',
];
export const rookgedragOptieRoker = SmokingBehaviour.roker;

const pulmoKlachtenCustomControlData = Koi.object({
    KNO_klachten_text: Koi.string().max(1000).allow(null),
    KNO_klachten_value: Koi.boolean().allow(null),
    aantal_exacerbaties_in_het_afgelopen_jaar: Koi.string().max(1000).allow(null),
    allergie_text: Koi.string().max(1000).allow(null),
    allergie_value: Koi.boolean().allow(null),
    astma_in_de_familie_text: Koi.string().max(1000).allow(null),
    astma_in_de_familie_value: Koi.boolean().allow(null),
    bekend_met_COPD_text: Koi.string().max(1000).allow(null),
    bekend_met_COPD_value: Koi.boolean().allow(null),
    bekend_met_astma_text: Koi.string().max(1000).allow(null),
    bekend_met_astma_value: Koi.boolean().allow(null),
    bekend_met_hartfalen_text: Koi.string().max(1000).allow(null),
    bekend_met_hartfalen_value: Koi.boolean().allow(null),
    beroep: Koi.string().max(1000).allow(null),
    hyperreactiviteit_text: Koi.string().max(1000).allow(null),
    hyperreactiviteit_value: Koi.boolean().allow(null),
    kortademig_s_nachts_text: Koi.string().max(1000).allow(null),
    kortademig_s_nachts_value: Koi.boolean().allow(null),
    maagklachten_text: Koi.string().max(1000).allow(null),
    maagklachten_value: Koi.boolean().allow(null),
    rookgedrag: Koi.string()
        .allow(null)
        .valid(...rookgedragOpties),
    slijm_opgeven_text: Koi.string().max(1000).allow(null),
    slijm_opgeven_value: Koi.boolean().allow(null),
});

export const pulmoKlachtenCustomControlDataSchema: ISchemaContainer<IPulmoKlachtenCustomControlDataValidated> = {
    schema: pulmoKlachtenCustomControlData.options({ convert: false, presence: 'required' }),
};

export interface IPulmoMrcCustomControlDataValidated {
    bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing: string;
    scoreMrc: number;
}

const pulmoMrcCustomControlData = Koi.object({
    bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing:
        Koi.string().max(1000),
    scoreMrc: Koi.number().precision(2).min(0).max(100),
});

export const pulmoMrcCustomControlDataSchema: ISchemaContainer<IPulmoMrcCustomControlDataValidated> = {
    schema: pulmoMrcCustomControlData.options({ convert: false, presence: 'required' }),
};

export interface IPulmoCcqCustomControlDataValidated {
    angstig_bezorgd_voor_de_volgende_benauwdheidsaanval: string;
    dagelijkse_activiteiten_u_zelf_aankleden_wassen: string;
    gehoest: string;
    kortademig_gedurende_lichamelijke_inspanning: string;
    kortademig_in_rust: string;
    matige_lichamelijke_activiteiten_wandelen_huishoudelijk_werk_boodschappen_doen: string;
    neerslachtig_vanwege_uw_ademhalingsproblemen: string;
    scoreCcqComplaints: number;
    scoreCcqCondition: number;
    scoreCcqEmotions: number;
    scoreCcqTotal: number;
    slijm_opgehoest: string;
    sociale_activiteiten_praten_omgaan_met_kinderen_vrienden_familie_bezoeken: string;
    zware_lichamelijke_activiteiten_trap_lopen_haasten_sporten: string;
}

const pulmoCcqCustomControlData = Koi.object({
    angstig_bezorgd_voor_de_volgende_benauwdheidsaanval: Koi.string().max(50),
    dagelijkse_activiteiten_u_zelf_aankleden_wassen: Koi.string().max(50),
    gehoest: Koi.string().max(50),
    kortademig_gedurende_lichamelijke_inspanning: Koi.string().max(50),
    kortademig_in_rust: Koi.string().max(50),
    matige_lichamelijke_activiteiten_wandelen_huishoudelijk_werk_boodschappen_doen: Koi.string().max(50),
    neerslachtig_vanwege_uw_ademhalingsproblemen: Koi.string().max(50),
    scoreCcqComplaints: Koi.number().precision(2).min(0).max(100),
    scoreCcqCondition: Koi.number().precision(2).min(0).max(100),
    scoreCcqEmotions: Koi.number().precision(2).min(0).max(100),
    scoreCcqTotal: Koi.number().precision(2).min(0).max(100),
    slijm_opgehoest: Koi.string().max(50),
    sociale_activiteiten_praten_omgaan_met_kinderen_vrienden_familie_bezoeken: Koi.string().max(50),
    zware_lichamelijke_activiteiten_trap_lopen_haasten_sporten: Koi.string().max(50),
});

export const pulmoCcqCustomControlDataSchema: ISchemaContainer<IPulmoCcqCustomControlDataValidated> = {
    schema: pulmoCcqCustomControlData.options({ convert: false, presence: 'required' }),
};

export interface IPulmoAcqCustomControlDataValidated {
    FEV1_procent_voorspeld: string;
    FEV1_voor_luchtwegverwijding: string | null;
    FEV1_voorspeld: string | null;
    hoe_ernstig_waren_uw_asmaklachten_bij_het_s_morgens_wakker_worden_gemiddeld_in_de_afgelopen_week: string;
    hoe_vaak_bent_u_per_nacht_gemiddeld_wakker_geworden_door_uw_astma_in_de_afgelopen_week: string;
    hoe_vaak_had_u_in_de_afgelopen_week_over_het_algemeen_een_piepende_ademhaling: string;
    // eslint-disable-next-line @stylistic/max-len
    hoe_veel_puffs_inhalaties_van_een_kortwerkend_luchtwegverwijdend_middel_bijv_ventolin_bricanyl_salbutamol_of_airomir_heeft_u_op_de_meeste_dagen_genomen_in_de_afgelopen_week: string;
    // eslint-disable-next-line @stylistic/max-len
    in_welke_mate_heeft_u_zich_over_het_algemeen_kortademig_gevoeld_in_de_afgelopen_week_ten_gevolge_van_uw_astma: string;
    in_welke_mate_werd_u_over_het_algemeen_in_de_afgelopen_week_door_uw_astma_beperkt_bij_uw_activiteiten: string;
    scoreAcq: number;
}

const pulmoAcqCustomControlData = Koi.object({
    FEV1_procent_voorspeld: Koi.string().max(50),
    FEV1_voor_luchtwegverwijding: Koi.numberAsString().decimal(',', 2).min(0).max(9),
    FEV1_voorspeld: Koi.numberAsString().decimal(',', 2).min(0).max(9),
    hoe_ernstig_waren_uw_asmaklachten_bij_het_s_morgens_wakker_worden_gemiddeld_in_de_afgelopen_week:
        Koi.string().max(50),
    hoe_vaak_bent_u_per_nacht_gemiddeld_wakker_geworden_door_uw_astma_in_de_afgelopen_week: Koi.string().max(50),

    hoe_vaak_had_u_in_de_afgelopen_week_over_het_algemeen_een_piepende_ademhaling: Koi.string().max(50),

    // eslint-disable-next-line @stylistic/max-len
    hoe_veel_puffs_inhalaties_van_een_kortwerkend_luchtwegverwijdend_middel_bijv_ventolin_bricanyl_salbutamol_of_airomir_heeft_u_op_de_meeste_dagen_genomen_in_de_afgelopen_week:
        Koi.string().max(50),

    in_welke_mate_heeft_u_zich_over_het_algemeen_kortademig_gevoeld_in_de_afgelopen_week_ten_gevolge_van_uw_astma:
        Koi.string().max(50),

    in_welke_mate_werd_u_over_het_algemeen_in_de_afgelopen_week_door_uw_astma_beperkt_bij_uw_activiteiten:
        Koi.string().max(50),
    scoreAcq: Koi.number().precision(2).min(0).max(100),
});

export const pulmoAcqCustomControlDataSchema: ISchemaContainer<IPulmoAcqCustomControlDataValidated> = {
    schema: pulmoAcqCustomControlData.options({ convert: false, presence: 'required' }),
};

// todo check if this is also applicable for the CareFusion spiro, or if this should be generalized to fit both spirometers (CS-1357)
export interface IImportedSpirometerData {
    afgerondOp: string | null;
    commentaar: string | null;
    FEV1_FVC_ratio_post_BD: string | null;
    FEV1_FVC_ratio_pre_BD: string | null;
    FEV1_FVC_voorspeld: string | null;
    FEV1_persoonlijk_best: string | null;
    FEV1_persoonlijk_best_van_voorspeld: string | null;
    FEV1_post_BD: string | null;
    FEV1_post_BD_van_voorspeld: string | null;
    FEV1_pre_BD: string | null;
    FEV1_pre_BD_van_voorspeld: string | null;
    FEV1_reversibiliteit: string | null;
    FEV1_voorspeld: string | null;
    FVC_post_B: string | null;
    FVC_post_BD_van_voorspeld: string | null;
    FVC_pre_BD: string | null;
    FVC_pre_BD_van_voorspeld: string | null;
    FVC_voorspeld: string | null;
    interpretatie: string | null;
    PEF_post_BD: string | null;
    PEF_pre_B: string | null;
    PEF_reversibiliteit: string | null;
}

export interface ITpcV1RequestCustomControlDataValidated {
    ACQ: IPulmoAcqCustomControlDataValidated | null;
    CCQ: IPulmoCcqCustomControlDataValidated | null;
    desktopProcessId?: string;
    files: IAssets;
    gender: Gender;
    gewicht: number | null;
    importedSpirometerData: IImportedSpirometerData | null;
    klachten: IPulmoKlachtenCustomControlDataValidated | null;
    lengte: number | null;
    MRC: IPulmoMrcCustomControlDataValidated | null;
    processError?: string;
    processFileError?: string;
    processStatus?: HardwareProcessStatus;
}

export const tpcV1FinalRequest = Koi.object().keys({
    ACQ: pulmoAcqCustomControlData.allow(null),
    CCQ: pulmoCcqCustomControlData.allow(null),
    MRC: pulmoMrcCustomControlData.allow(null),
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
    desktopProcessId: Koi.string().uuid().optional().allow(null),
    files: assets,
    gender: Koi.koi().enum(Gender).optional(),
    gewicht: Koi.number().allow(null),
    importedSpirometerData: Koi.object({
        FEV1_FVC_ratio_post_BD: Koi.string().max(50).allow(null),
        FEV1_FVC_ratio_pre_BD: Koi.string().max(50).allow(null),
        FEV1_FVC_voorspeld: Koi.string().max(50).allow(null),
        FEV1_persoonlijk_best: Koi.string().max(50).allow(null),
        FEV1_persoonlijk_best_van_voorspeld: Koi.string().max(50).allow(null),
        FEV1_post_BD: Koi.string().max(50).allow(null),
        FEV1_post_BD_van_voorspeld: Koi.string().max(50).allow(null),
        FEV1_pre_BD: Koi.string().max(50).allow(null),
        FEV1_pre_BD_van_voorspeld: Koi.string().max(50).allow(null),
        FEV1_reversibiliteit: Koi.string().max(50).allow(null),
        FEV1_voorspeld: Koi.string().max(50).allow(null),
        FVC_post_BD: Koi.string().max(50).allow(null),
        FVC_post_BD_van_voorspeld: Koi.string().max(50).allow(null),
        FVC_pre_BD: Koi.string().max(50).allow(null),
        FVC_pre_BD_van_voorspeld: Koi.string().max(50).allow(null),
        FVC_voorspeld: Koi.string().max(50).allow(null),
        PEF_post_BD: Koi.string().max(50).allow(null),
        PEF_pre_BD: Koi.string().max(50).allow(null),
        PEF_reversibiliteit: Koi.string().max(50).allow(null),
        afgerondOp: Koi.koi().date(),
        commentaar: Koi.string().max(1000).allow(null),
        interpretatie: Koi.string().max(1000).allow(null),
    }).allow(null),
    klachten: pulmoKlachtenCustomControlData.allow(null),
    lengte: Koi.number().allow(null),
    processError: Koi.string().max(1000).optional(),
    processFileError: Koi.string().max(1000).optional(),
    processStatus: Koi.koi().enum(HardwareProcessStatus).optional(),
});

export const tpcV1DraftRequest = tpcV1FinalRequest.keys({
    conclusie_diagnose: Koi.any().allow(null),
});

export const tpcV1RequestSchema: ISchemaContainer<ITpcV1RequestCustomControlDataValidated> = {
    schema: tpcV1FinalRequest.options({ convert: false, presence: 'required' }),
};

export interface ITpoV1ProcedureValidated extends ITpcV1RequestCustomControlDataValidated {
    datum_afname_functieonderzoek: string;
    keten_DBC_COPD_astma: boolean;
    spirogram_inc_reversibiliteitstest: boolean;
}

export const tpoV1FinalProcedure = tpcV1FinalRequest.keys({
    datum_afname_functieonderzoek: Koi.koi().date(),
    keten_DBC_COPD_astma: Koi.boolean(),
    spirogram_inc_reversibiliteitstest: Koi.boolean(),
});

export const tpoV1ProcedureSchema: ISchemaContainer<ITpoV1ProcedureValidated> = {
    schema: tpoV1FinalProcedure.options({ convert: false, presence: 'required' }),
};

export const tpoV1DraftProcedure = tpoV1FinalProcedure.concat(
    Koi.object({
        datum_afname_functieonderzoek: Koi.any().allow(null),
    })
);

export const tpoV1DraftProcedureSchema: ISchemaContainer<never> = {
    schema: tpoV1DraftProcedure.options({ convert: false, presence: 'required' }),
};

export interface IPlaceHolderCustomControlDataValidated {
    placeholder: string | null;
}

export const placeHolderCustomControlDataSchema: ISchemaContainer<IPlaceHolderCustomControlDataValidated> = {
    schema: Koi.any(),
};

export interface ITpoV1DiagnosticReportExaminationReportCustomControlDataValidated {
    diagnostische_overweging: string;
    ernst_obstructie: string | null;
    obstructief: string;
    restrictief: string;
    reversible: string;
}

export const tpoV1FinalDiagnosticReport = Koi.object().keys({
    diagnostische_overweging: Koi.string().max(100),
    ernst_obstructie: Koi.when('obstructief', {
        is: 'Ja',
        otherwise: Koi.valid(null),
        then: Koi.string().valid('Licht', 'Matig', 'Ernstig', 'Zeer ernstig'),
    }),
    obstructief: Koi.string().valid('Ja', 'Nee'),
    restrictief: Koi.string().valid('Mogelijk', 'Nee'),
    reversible: Koi.string().valid('Nee', 'Ja', 'Niet bepaald'),
});

export const tpoV1DraftDiagnosticReport = tpoV1FinalDiagnosticReport.concat(
    Koi.object({
        diagnostische_overweging: Koi.any().allow(null),
        ernst_obstructie: Koi.any().allow(null),
        obstructief: Koi.any().allow(null),
        restrictief: Koi.any().allow(null),
        reversible: Koi.any().allow(null),
    })
);

export interface ICardioPerfectDataValidated {
    gender: GenderSpirometer;
    gewicht: number;
    lengte: number;
}

export const cardioPerfectSchema: ISchemaContainer<ICardioPerfectDataValidated> = {
    schema: Koi.object({
        cardioPerfectEthnicGroup: Koi.koi().enum(CardioPerfectEthnicGroup),
        gender: Koi.koi().enum(GenderSpirometer),
        gewicht: Koi.number().min(0).max(10000),
        lengte: Koi.number().min(0).max(10000),
    }).options({ convert: false, presence: 'required' }),
};

export interface ICareFusionDataValidated {
    ethnicGroup: CareFusionEthnicGroup;
    gender: GenderSpirometer;
    gewicht: number;
    lengte: number;
    rokerDuur?: string;
    rookgedrag: string;
}

export const careFusionSchema: ISchemaContainer<ICareFusionDataValidated> = {
    schema: Koi.object({
        careFusionEthnicGroup: Koi.koi().enum(CareFusionEthnicGroup),
        gender: Koi.koi().enum(GenderSpirometer),
        gewicht: Koi.number().min(0).max(10000),
        lengte: Koi.number().min(0).max(10000),
        rokerDuur: Koi.string().when('rookgedrag', {
            is: rookgedragOptieRoker,
            otherwise: Koi.forbidden(),
            then: Koi.valid(...rokerDuurOpties),
        }),
        rookgedrag: Koi.string().valid(...rookgedragOpties),
    }).options({ convert: false, presence: 'required' }),
};

export interface ITpcV1DiagnosticReportConsultReportCustomControlDataValidated {
    aanvullende_vragen: string | null;
    advies: string | null;
    bezoek_aan_specialist_nodig: string;
    conclusie_diagnose: ConclusieDiagnoseOption;
    diagnose_astma_value: string;
    diagnose_COPD_value: string;
}

export const tpcV1FinalDiagnosticReport = Koi.object().keys({
    aanvullende_vragen: Koi.string().max(10000).allow(null),
    advies: Koi.when('bezoek_aan_specialist_nodig', {
        is: 'Zie advies',
        otherwise: Koi.string().max(10000).allow(null),
        then: Koi.string().max(10000).required(),
    }),
    bezoek_aan_specialist_nodig: Koi.string().valid('Ja', 'Nee', 'Zie advies', 'Niet van toepassing'),
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption),
    diagnose_COPD_text: Koi.when('diagnose_COPD_value', {
        is: 'Anders',
        otherwise: Koi.valid(null),
        then: Koi.string().max(10000).required(),
    }),
    diagnose_COPD_value: Koi.when('conclusie_diagnose', {
        is: ConclusieDiagnoseOption.ja,
        otherwise: Koi.valid(null),
        then: Koi.string().valid('Ja', 'Nee', 'Onzeker', 'Anders').required(),
    }),
    diagnose_astma_text: Koi.when('diagnose_astma_value', {
        is: 'Anders',
        otherwise: Koi.valid(null),
        then: Koi.string().max(10000).required(),
    }),
    diagnose_astma_value: Koi.when('conclusie_diagnose', {
        is: ConclusieDiagnoseOption.ja,
        otherwise: Koi.valid(null),
        then: Koi.string().valid('Ja', 'Nee', 'Onzeker', 'Anders').required(),
    }),
});

export const tpcV1DraftDiagnosticReport = tpcV1FinalDiagnosticReport.concat(
    Koi.object({
        advies: Koi.any().allow(null),
        bezoek_aan_specialist_nodig: Koi.any().allow(null),
        conclusie_diagnose: Koi.any().allow(null),
        diagnose_COPD_text: Koi.any().allow(null),
        diagnose_COPD_value: Koi.any().allow(null),
        diagnose_astma_text: Koi.any().allow(null),
        diagnose_astma_value: Koi.any().allow(null),
    })
);

export interface IKlachten {
    aantal_exacerbaties_in_het_afgelopen_jaar: string | null;
    allergie_text: string | null;
    allergie_value: boolean | null;
    astma_in_de_familie_text: string | null;
    astma_in_de_familie_value: boolean | null;
    bekend_met_astma_text: string | null;
    bekend_met_astma_value: boolean | null;
    bekend_met_COPD_text: string | null;
    bekend_met_COPD_value: boolean | null;
    bekend_met_hartfalen_text: string | null;
    bekend_met_hartfalen_value: boolean | null;
    beroep: string | null;
    hyperreactiviteit_text: string | null;
    hyperreactiviteit_value: boolean | null;
    KNO_klachten_text: string | null;
    KNO_klachten_value: boolean | null;
    kortademig_s_nachts_text: string | null;
    kortademig_s_nachts_value: boolean | null;
    maagklachten_text: string | null;
    maagklachten_value: boolean | null;
    rookgedrag: string | null;
    slijm_opgeven_text: string | null;
    slijm_opgeven_value: boolean | null;
}
export interface IMrc {
    bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing:
        | string
        | null;
    scoreMrc: number | null;
}
